import { toAttendee } from '@tunasong/calendar'
import { shortUuid } from '@tunasong/models'
import { getDefaultYDocString } from '@tunasong/plugin-lib'
import type { Calendar, CalendarEvent, ManyOfManyPoll, Persisted, PollResponse, Profile } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { getLiveSlotIndices, getSlotEnd } from './date-slot.js'

export interface BuildCalendarEventsArgs {
  poll: Persisted<ManyOfManyPoll>
  responses: Persisted<PollResponse>[]
  members: Profile[]
  organizer: Profile
}

/**
 * One `calendarevent` per winning slot, with the yes-voters as attendees.
 *
 * Slots store `z.iso.datetime()`, the same shape as `calendarEventSchema.start`, so this is a
 * field copy rather than a conversion. A maybe is not an attendance and never becomes an
 * attendee.
 */
export const buildCalendarEvents = ({ poll, responses, members, organizer }: BuildCalendarEventsArgs) => {
  /** Via getLiveSlotIndices, so a tombstoned slot cannot become an event even if marked final. */
  const winners = getLiveSlotIndices(poll).filter(idx => poll.finalOption?.[idx])

  return winners.reduce<CalendarEvent[]>((events, idx) => {
    const slot = poll.pollOptions[idx]
    if (!isDateSlot(slot)) {
      return events
    }

    const attendees = responses
      .filter(response => response.availability?.[String(idx)] === 'yes')
      .map(response => members.find(member => member.userId === response.userId))
      .filter((member): member is Profile => Boolean(member))
      .map(toAttendee)

    return [
      ...events,
      {
        // Set client-side so the calendar updates optimistically.
        id: shortUuid(),
        type: 'calendarevent',
        name: poll.name,
        start: slot.start,
        end: getSlotEnd(slot),
        organizer: toAttendee(organizer),
        attendees,
        yDoc: getDefaultYDocString(),
      } satisfies CalendarEvent,
    ]
  }, [])
}

/** `Add all to Band calendar` when the target resolved to one; generic otherwise. */
export const formatAddToCalendarLabel = (calendar?: Pick<Calendar, 'name'>) =>
  calendar?.name ? `Add all to ${calendar.name}` : 'Add all to calendar'
